import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import FormX from 'sb-formx'
import actions from 'redux/actions'
import { connect } from 'redaction'
import { snowplow, constants, compose } from 'common/helpers'
import { required } from 'helpers/validation'
import { FormattedHTMLMessage } from 'sb-react-intl'

import injectGlobals from 'decorators/injectGlobals'

import Box from 'components/_new/layout/Box/Box'
import Modal from 'components/modal/Modal/Modal'
import Field from 'components/_new/forms/Field/Field'
import Button from 'components/buttons/Button/Button'
import AgeSelect from 'components/_new/compositions/forms/AgeSelect/AgeSelect'
import SkinTypeSelect from 'components/_new/compositions/forms/SkinTypeSelect/SkinTypeSelect'

import ShareToggler from '../ShareToggler/ShareToggler'
import SelectRating from '../SelectRating/SelectRating'

import messages from './messages'
import s from './ReviewComposerModal.scss'


const form = new FormX({
  name: 'ReviewForm',
  fields: {
    title: [ required ],
    text: [ required ],
    rating: [ required ],
    ageCategory: [],
    skinType: [],
  },
})

class ReviewComposerModal extends Component {

  static propTypes = {
    name: PropTypes.string,
    title: PropTypes.object,
    product: PropTypes.shape({
      id: PropTypes.number,
      img: PropTypes.string,
      name: PropTypes.string,
      brand: PropTypes.string,
      category: PropTypes.string,
    }).isRequired,
    shouldBeShared: PropTypes.shape({
      facebook: PropTypes.bool,
      twitter: PropTypes.bool,
    }).isRequired,
    data: PropTypes.shape({
      reviewId: PropTypes.number,
      withItemList: PropTypes.bool,
      initialRating: PropTypes.number,
      productTypeGroup: PropTypes.string,
      onReviewAdded: PropTypes.func,
    }),
    isNewParamsToReviewsEnabled: PropTypes.bool,
  }

  static defaultProps = {
    name: constants.modals.review.ReviewComposer,
    data: {
      withItemList: false,
    },
  }

  constructor({ skinType, ageCategory, data: { initialRating } }) {
    super()

    form.setInitialValues({ skinType, ageCategory, rating: initialRating })

    this.state = {
      isFetching: false,
    }
  }

  componentDidMount() {
    actions.models.facebook.init()
  }

  componentWillUnmount() {
    form.unsetValues()
  }

  setFetching = (status) => {
    this.setState({ isFetching: status })
  }

  submit = (values) => {
    const {
      name, product: { id: productId }, shouldBeShared,
      data: { withItemList, productTypeGroup, onReviewAdded },
    } = this.props
    const { ageCategory, skinType } = values

    snowplow.track(constants.snowplowEvents.reviewSubmit, { ...shouldBeShared })

    this.setFetching(true)

    actions.models.review.add({
      ...values,
      productId,
    })
      .then(
        ({ id }) => {
          if (typeof onReviewAdded === 'function') {
            onReviewAdded(productId)
          }

          if (productTypeGroup === constants.productTypeGroups.perfume) {
            actions.models.modals.open(constants.modals.review.ReviewTags, {
              productId,
              withItemList,
              reviewId: id,
              // We need reload reviews to update the review we created with tags from ReviewsTags result
              onReviewTagsVoted: () => {
                if (typeof onReviewAdded === 'function') {
                  onReviewAdded(productId)
                }
              },
            })
          }
          else if (productTypeGroup === constants.productTypeGroups.skincare) {
            actions.models.modals.open(constants.modals.review.Recommendations, {
              isSkincareGroup: true,
            })
          }

          if (ageCategory || skinType) {
            actions.models.me.setAgeAndSkinType({ ageCategory, skinType })
          }

          actions.models.modals.close(name)
        },
        () => this.setFetching(false)
      )
  }

  handleSubmit = () => {
    form.submit()
      .then(this.submit)
  }

  render() {
    const { isFetching } = this.state
    const {
      name: modalName, isNewParamsToReviewsEnabled,
      data: { productTypeGroup },
      product: { img, brand, name, category },
    } = this.props

    const commentPlaceholder = (
      productTypeGroup === constants.productTypeGroups.makeup
        ? messages.makeupReviewPlaceholder
        : messages.reviewPlaceholder
    )

    return (
      <Modal className={s.modal} name={modalName}>
        <FormattedHTMLMessage className={s.mainTitle} tag="div" {...messages.mainTitle} />
        <img className={s.previewImage} src={img} alt={`${category} preview`} />
        <div className={s.brand}>{brand}</div>
        <div className={s.name}>{name}</div>
        <div className={s.category}>{category}</div>
        <div className={s.rowItem}>
          <SelectRating field={form.fields.rating} />
          <Field
            className={s.rowItem}
            placeholder={messages.titleLabel}
            field={form.fields.title}
            standardView
          />
          <Field
            className={s.rowItem}
            label={messages.reviewLabel}
            field={form.fields.text}
            placeholder={commentPlaceholder}
            standardView
            rows={9}
          />
          {
            isNewParamsToReviewsEnabled ? (
              <Fragment>
                <FormattedHTMLMessage className={cx(s.hint, s.rowItem)} tag="div" {...messages.hint} />
                <div className={cx(s.rowItem, s.additionalBox)}>
                  <div style={{ display: 'flex' }}>
                    <AgeSelect field={form.fields.ageCategory} />
                    <Box ml="24">
                      <SkinTypeSelect field={form.fields.skinType} />
                    </Box>
                  </div>
                  <ShareToggler className={s.shareToggler} />
                </div>
              </Fragment>
            ) : (
              <ShareToggler className={cx(s.rowItem, s.shareToggler)} />
            )
          }
          <div className={s.buttons}>
            <Button
              title={messages.submitButtonTitle}
              loading={isFetching}
              brand
              xlg
              doublePadding
              onClick={this.handleSubmit}
            />
          </div>
        </div>
      </Modal>
    )
  }
}


export default compose(
  injectGlobals(({ getFeatureToggle }) => ({
    isNewParamsToReviewsEnabled: getFeatureToggle(constants.features.newParamsToReviews),
  })),
  connect({
    product: 'models.review.product',
    shouldBeShared: 'models.review.shouldBeShared',
    skinType: 'models.me.skinType',
    ageCategory: 'models.me.ageCategory',
  }),
)(ReviewComposerModal)
